import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useKeepAliveTabVisible } from '../../contexts/KeepAliveTabVisibilityContext';

/** Material standard easing — desktop / fine-pointer modals. */
export const AUTO_HEIGHT_WEB_MS = 320;
export const AUTO_HEIGHT_WEB_EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
export const AUTO_HEIGHT_CSS_TRANSITION = `height ${AUTO_HEIGHT_WEB_MS}ms ${AUTO_HEIGHT_WEB_EASE}`;

/** Snappier curve shared with Collapsible — reads better on touch devices. */
export const AUTO_HEIGHT_MOBILE_MS = 240;
export const AUTO_HEIGHT_MOBILE_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
export const AUTO_HEIGHT_MOBILE_TRANSITION = `height ${AUTO_HEIGHT_MOBILE_MS}ms ${AUTO_HEIGHT_MOBILE_EASE}`;

function measureHeight(el) {
  if (!el) return 0;
  // scrollHeight is more reliable than contentRect on mobile Safari when text
  // wraps, inputs focus, or nested blocks reflow mid-transition.
  return el.scrollHeight;
}

/**
 * Smoothly transitions outer height when inner content changes size.
 * ResizeObserver-driven — catches step swaps, disclosure toggles, list
 * morphs, and preview text updates. Initial mount is non-animated so the
 * shell doesn't pop open from 0 → content on first paint.
 *
 * Use one instance per animated shell (do not nest — nested observers jitter).
 *
 * `transition` overrides the fine-pointer tween; `touchTransition` (opt-in,
 * KAN-562 role welcome) overrides the coarse-pointer one. Omitted = the
 * shared defaults, unchanged.
 */
const INNER = { display: 'flow-root' };
const INNER_BOTTOM = { display: 'flow-root', flexShrink: 0 };

export default function AutoHeight({
  children, transition = AUTO_HEIGHT_CSS_TRANSITION, touchTransition = AUTO_HEIGHT_MOBILE_TRANSITION, clip = false,
  anchor = 'top',
}) {
  const innerRef = useRef(null);
  const syncFrameRef = useRef(null);
  const [height, setHeight] = useState('auto');
  const [transitionsEnabled, setTransitionsEnabled] = useState(false);

  const reduceMotion = useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  const preferMobileTiming = useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  }, []);

  // A kept-alive tab (phone/tablet) is hidden with display:none, where every
  // box measures 0 — tweening to that 0 made the shell grow back from nothing
  // on every return to the tab (KAN-1024). Keep the last real height while
  // hidden; the ResizeObserver fires again once the tab is shown.
  const visible = useKeepAliveTabVisible();
  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  const applyHeight = useCallback(() => {
    if (!visibleRef.current) return;
    const next = measureHeight(innerRef.current);
    // 0 is a real answer: a list that just emptied MUST tween down to nothing.
    // The old `if (!next) return` skipped it, so every emptied list (extras,
    // chemicals, services…) kept its last height as a blank band — the
    // "elements don't go back in place" bug (founder, 2026-09-11).
    if (!Number.isFinite(next)) return;
    setHeight(next);
  }, []);

  const scheduleSync = useCallback(() => {
    if (syncFrameRef.current != null) return;
    syncFrameRef.current = requestAnimationFrame(() => {
      syncFrameRef.current = null;
      applyHeight();
    });
  }, [applyHeight]);

  useEffect(() => {
    if (!innerRef.current || typeof ResizeObserver === 'undefined') return undefined;
    applyHeight();

    const raf = requestAnimationFrame(() => setTransitionsEnabled(true));

    const observer = new ResizeObserver(() => scheduleSync());
    observer.observe(innerRef.current);

    return () => {
      cancelAnimationFrame(raf);
      if (syncFrameRef.current != null) cancelAnimationFrame(syncFrameRef.current);
      observer.disconnect();
    };
  }, [applyHeight, scheduleSync]);

  const heightTransition = transitionsEnabled && !reduceMotion
    ? (preferMobileTiming ? touchTransition : transition)
    : 'none';

  return (
    <div
      style={{
        height: typeof height === 'number' ? `${height}px` : height,
        // Never shrink to fit a flex parent. `overflow: hidden` drops a flex
        // item's automatic min-size to 0, so inside a column-flex scroll body
        // this shell got squeezed to the panel's max height and CLIPPED the
        // tail — the body saw no overflow and would not scroll (Resend
        // estimate "Correo" step, 2026-09-23). The panel's maxHeight is the
        // cap; the scroll region scrolls; this shell only tweens.
        flexShrink: 0,
        transition: heightTransition,
        // `clip` (opt-in): same visual clipping but NOT a scroll container, so a
        // `position: sticky` child still sticks to the real scroller above
        // (setup guide's live invoice preview). Default stays 'hidden'.
        overflow: clip ? 'clip' : 'hidden',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
        overflowAnchor: 'none',
        // `anchor="bottom"` (opt-in, KAN-763): the content rests on the box's
        // BOTTOM edge while the height tweens, so a bottom-pinned footer whose
        // top part folds away keeps its buttons still (the estimate rail's
        // summary fold). Default (top) is unchanged.
        ...(anchor === 'bottom' ? { display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' } : null),
      }}
    >
      {/* flow-root: children's margins stay INSIDE the measured box. Without
          it a first child's top margin collapses out of this div, so
          scrollHeight under-counts by that margin while the content is still
          pushed down by it — the outer overflow:hidden then clips exactly
          that much off the bottom (2026-09-27: the bulk-send footer lost its
          bottom padding on a phone). */}
      <div ref={innerRef} style={anchor === 'bottom' ? INNER_BOTTOM : INNER}>{children}</div>
    </div>
  );
}
